// Feedback page (/feedback/).
// Star rating splits the flow:
//   * 4–5 stars → redirect to the public Google review URL.
//   * 1–3 stars → reveal the feedback form; submit goes to the Cloudflare
//                 Worker /feedback endpoint, which emails the team via Resend
//                 (same pipeline as /lead and /save-quote).
//
// Turnstile is rendered lazily — only when the form is revealed — to keep
// the captcha off the page for happy customers heading to Google Reviews.

import { onReady } from "#public/utils/on-ready.js";

const GOOGLE_REVIEW_URL = "https://g.page/r/CQWLAQhniQnhEBM/review";

const setError = (form, msg) => {
  const el = form.querySelector("[data-fb-error]");
  if (el) el.textContent = msg || "";
};

const setStatus = (msg) => {
  const el = document.querySelector("[data-fb-status]");
  if (!el) return;
  el.textContent = msg || "";
  el.hidden = !msg;
};

const setSubmitting = (form, isSubmitting) => {
  const btn = form.querySelector("[data-fb-submit]");
  if (!btn) return;
  btn.disabled = isSubmitting;
  btn.textContent = isSubmitting ? "Sending…" : "Send";
};

const paintStars = (stars, rating) => {
  for (const star of stars) {
    const value = Number(star.getAttribute("data-rating"));
    star.classList.toggle("is-active", value <= rating);
    star.setAttribute("aria-pressed", value === rating ? "true" : "false");
  }
};

const lockStars = (stars) => {
  for (const star of stars) {
    star.disabled = true;
  }
};

let TURNSTILE_WIDGET_ID = null;

const whenTurnstileReady = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    if (window.turnstile) return resolve(window.turnstile);
    const start = Date.now();
    const poll = () => {
      if (window.turnstile) return resolve(window.turnstile);
      if (Date.now() - start > 10000) return resolve(null);
      setTimeout(poll, 100);
    };
    poll();
  });

const renderTurnstile = async (placeholderId, sitekey) => {
  const placeholder = document.getElementById(placeholderId);
  if (!placeholder || !sitekey) return;
  if (TURNSTILE_WIDGET_ID) return;
  const turnstile = await whenTurnstileReady();
  if (!turnstile) return;
  try {
    const id = turnstile.render(`#${placeholderId}`, {
      sitekey,
      action: "feedback",
      theme: "light",
    });
    if (id) TURNSTILE_WIDGET_ID = id;
  } catch (_e) {
    // No-op: render can throw if already mounted.
  }
};

const resetTurnstile = () => {
  if (typeof window === "undefined" || !window.turnstile) return;
  if (!TURNSTILE_WIDGET_ID) return;
  try {
    window.turnstile.reset(TURNSTILE_WIDGET_ID);
  } catch (_e) {
    // No-op.
  }
};

const buildPayload = (form) => {
  const fd = new FormData(form);
  const ts = form.querySelector("[name='cf-turnstile-response']");
  return {
    rating: Number(form.getAttribute("data-fb-rating") || 0),
    name: String(fd.get("name") || "").trim(),
    email: String(fd.get("email") || "").trim(),
    feedback: String(fd.get("feedback") || "").trim(),
    website: String(fd.get("website") || ""),
    turnstile_token: ts && ts.value ? ts.value : "",
  };
};

const validatePayload = (payload) => {
  if (!payload.feedback) return "Please tell us what went wrong.";
  if (!payload.name) return "Please enter your name.";
  if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
    return "Please enter a valid email address.";
  }
  if (!payload.turnstile_token) {
    return "Please wait for the security check to finish, then submit again.";
  }
  return null;
};

const submitForm = async (form, onSuccess) => {
  setError(form, "");
  const payload = buildPayload(form);
  const err = validatePayload(payload);
  if (err) {
    setError(form, err);
    return;
  }

  const workerUrl = (form.getAttribute("data-fb-worker-url") || "").trim();
  if (!workerUrl) {
    setError(form, "Form isn't configured — please email us instead.");
    return;
  }

  setSubmitting(form, true);
  let ok = false;
  try {
    const response = await fetch(`${workerUrl.replace(/\/$/, "")}/feedback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    ok = response.ok;
  } catch (_e) {
    ok = false;
  }

  setSubmitting(form, false);

  if (!ok) {
    resetTurnstile();
    setError(
      form,
      "Sorry, something went wrong sending your feedback. Please try again or call us on 03331 126993.",
    );
    return;
  }

  // Hide the wrap, not the form — `.fb-form { display: grid }` would
  // override the [hidden] attribute on the form element itself.
  const wrap = form.closest("[data-fb-form-wrap]");
  if (wrap) wrap.hidden = true;
  setStatus(
    "Thanks for your feedback — we'll be in touch shortly to put things right.",
  );
  if (onSuccess) onSuccess();
};

const init = () => {
  const root = document.querySelector("[data-feedback-page]");
  if (!root) return;

  const stars = root.querySelectorAll("[data-fb-star]");
  const formWrap = root.querySelector("[data-fb-form-wrap]");
  const form = root.querySelector("[data-fb-form]");
  const title = root.querySelector("[data-fb-title]");
  const intro = root.querySelector("[data-fb-intro]");
  const sitekey = root.getAttribute("data-fb-turnstile-key") || "";

  let locked = false;

  for (const star of stars) {
    star.addEventListener("click", () => {
      if (locked) return;
      const rating = Number(star.getAttribute("data-rating"));
      paintStars(stars, rating);
      if (form) form.setAttribute("data-fb-rating", String(rating));

      if (rating >= 4) {
        locked = true;
        lockStars(stars);
        window.location.href = GOOGLE_REVIEW_URL;
        return;
      }

      if (title) title.textContent = "We're sorry to hear that";
      if (intro) {
        intro.textContent = "Tell us what went wrong and we'll be in touch.";
      }
      if (formWrap) formWrap.hidden = false;
      renderTurnstile("fb-turnstile", sitekey);
    });
  }

  if (form) {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      submitForm(form, () => {
        locked = true;
        lockStars(stars);
      });
    });
  }
};

onReady(init);
